import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';

export interface UserPermissions {
  isSuperAdmin: boolean;
  isOwner: boolean;
  canCreateWorkspaces: boolean;
  canManageAllWorkspaces: boolean;
  canViewAllWorkspaces: boolean;
  canManageWorkspace: (workspaceId: string) => boolean;
  canViewWorkspace: (workspaceId: string) => boolean;
  canInviteMembers: (workspaceId: string) => boolean;
  canManageModules: (workspaceId: string) => boolean;
  managedWorkspaceIds: string[];
  accessibleWorkspaceIds: string[];
  hasFullAccess: boolean;
  roles: string[];
  checkPermission: (permission: string) => boolean;
}

export function usePermissions(): {
  permissions: UserPermissions | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { user } = useAuth();

  const { data: permissions, isLoading, error } = useQuery({
    queryKey: ['user-permissions', user?.id],
    queryFn: async (): Promise<UserPermissions> => {
      if (!user?.id) throw new Error('User not authenticated');

      // Static mode: treat the logged-in user as owner for now.
      const isSuperAdmin = true;
      const managedWorkspaceIds: string[] = [];
      const accessibleWorkspaceIds: string[] = [];

      const canManageWorkspace = (_workspaceId: string) => true;
      const canViewWorkspace = (_workspaceId: string) => true;
      const canInviteMembers = (_workspaceId: string) => true;
      const canManageModules = (_workspaceId: string) => true;

      return {
        isSuperAdmin,
        isOwner: isSuperAdmin,
        canCreateWorkspaces: true,
        canManageAllWorkspaces: true,
        canViewAllWorkspaces: true,
        canManageWorkspace,
        canViewWorkspace,
        canInviteMembers,
        canManageModules,
        managedWorkspaceIds,
        accessibleWorkspaceIds,
        hasFullAccess: true,
        roles: ['owner'],
        checkPermission: (_permission: string) => true,
      };
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2
  });

  return {
    permissions,
    isLoading,
    error: error as Error | null
  };
}
